import { memo, useCallback, useMemo } from 'react';
import { Button } from '../../../components/Button';
import { LedgerRow } from '../../../components/CreditLedger/LedgerRow';
import { Modal } from '../../../components/Modal';
import { useDownloadInvoice } from '../../../hooks/useDownloadInvoice';
import { T } from '../../../translation/src';
import { CreditLedgerEntrySource, useOrganisationCreditLedgerQuery } from '../../../types/graphqlTypes';

const PAGE_SIZE = 50;

export interface TransactionsModalProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
}
const TransactionsModal_: React.FC2<TransactionsModalProps> = ({ onClose, open, organisationId }) => {
  const { data, fetchMore, loading } = useOrganisationCreditLedgerQuery({
    variables: { organisationId, pagination: { limit: PAGE_SIZE } },
    skip: !open,
    fetchPolicy: 'cache-and-network',
  });

  // Rows whose invoice sales has sent carry its number, as a link that downloads the PDF.
  const { downloadInvoice, downloadingEntryId } = useDownloadInvoice({ organisationId });

  const page = data?.organisationCreditLedger;
  // Monthly allowances are not purchases; the subscription header on the page covers them.
  const entries = useMemo(() => (page?.data || []).filter((entry) => entry.source !== CreditLedgerEntrySource.MonthlyAllowance), [page]);
  const nextCursor = page?.nextCursor;
  const onLoadMore = useCallback(() => {
    if (!nextCursor) return;
    void fetchMore({
      variables: { organisationId, pagination: { limit: PAGE_SIZE, cursor: nextCursor } },
      updateQuery: (previous, { fetchMoreResult }) => ({
        organisationCreditLedger: { ...fetchMoreResult.organisationCreditLedger, data: [...previous.organisationCreditLedger.data, ...fetchMoreResult.organisationCreditLedger.data] },
      }),
    });
  }, [fetchMore, nextCursor, organisationId]);

  return (
    <Modal.Container open={open} onClose={onClose} panelClassName="w-full max-w-lg">
      <Modal.Header onClose={onClose}>
        <T _str="purchase history" swc />
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col min-h-40 max-h-96 overflow-y-auto">
          {entries.length === 0 && (
            <div className="flex items-center justify-center grow text-sm text-gray-500">
              <T _str="no transactions yet" swc />
            </div>
          )}
          {entries.map((entry) => (
            <LedgerRow key={entry.id} entry={entry} onDownloadInvoice={downloadInvoice} downloading={downloadingEntryId === entry.id} />
          ))}
          {page?.hasMore && (
            <div className="flex justify-center py-2">
              <Button variant="tertiary" size="xs" onClick={onLoadMore} loading={loading}>
                <T _str="load more" swc />
              </Button>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal.Container>
  );
};

export const TransactionsModal = memo(TransactionsModal_);
